/**
 * /admin/games — Admin game enable/disable toggle (Fase 4 N-10).
 *
 * Reads the games list from /api/admin/games (which itself reads
 * registry.json + game_state). Each row has a Switch primitive that
 * calls PATCH /api/admin/games/:gameId/toggle. Filter chips group by
 * visibility (public / classroom).
 */

import type { ReactNode } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { freshSessionQuery } from '../../lib/api/queries.js';
import {
  CatalogTogglePanel,
  type CatalogItemBase,
  type CatalogColumn,
  type FilterOption,
} from '../../components/admin/CatalogTogglePanel.js';

interface GameRow extends CatalogItemBase {
  /** Backend identifier on the wire — the catalog API returns `gameId`
   *  rather than the generic `id`. The route remaps it to `id` so the
   *  panel's mutation path resolves correctly. */
  gameId: string;
  slug: string | null;
  sdGrade: number | null;
  levelCount: number;
  visibility: 'public' | 'classroom';
  displayStatus: string | null;
}

const visibilityFilters: FilterOption[] = [
  {
    label: 'Publik',
    value: 'public',
    matches: (item, v) => (item as unknown as GameRow).visibility === v,
  },
  {
    label: 'Kelas',
    value: 'classroom',
    matches: (item, v) => (item as unknown as GameRow).visibility === v,
  },
];

const columns: CatalogColumn<GameRow>[] = [
  { label: 'Visibilitas', render: (g) => g.visibility },
  { label: 'Kelas', render: (g) => (g.sdGrade !== null ? `Kelas ${g.sdGrade}` : '—') },
  { label: 'Level', render: (g) => g.levelCount },
];

export const Route = createFileRoute('/admin/games')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role === 'tutor' && user.isApprovedTutor) {
      throw redirect({ to: '/dashboard' });
    }
    if (user.role === 'tutor' || user.kind === 'student') {
      throw redirect({ to: '/me' });
    }
    if (user.role !== 'admin') {
      throw redirect({ to: '/' });
    }
    return null;
  },
  component: AdminGamesPage,
});

function AdminGamesPage(): ReactNode {
  const fetchJson = async <T,>(input: RequestInfo, init?: RequestInit): Promise<T> => {
    const res = await fetch(input, {
      ...init,
      credentials: 'include',
      headers: { ...(init?.headers ?? {}), accept: 'application/json' },
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: { message?: string } };
      throw new Error(body.error?.message ?? `HTTP ${res.status}`);
    }
    return (await res.json()) as T;
  };

  // Wrap fetchJson to (1) convert the { games: [...] } response shape into
    // the generic { items: [...] } shape CatalogTogglePanel expects, and
    // (2) normalize the per-row `gameId` to `id` so the panel's toggle path
    // resolves correctly (panel reads `item.id` for the route param).
    const wrappedFetch = async <T,>(input: RequestInfo, init?: RequestInit): Promise<T> => {
      const res = await fetchJson<{ ok: boolean; data: { games: GameRow[]; total: number } }>(
        input,
        init,
      );
      return {
        ok: res.ok,
        data: {
          items: res.data.games.map((g) => ({
            ...g,
            id: g.gameId ?? g.id,
          })),
          total: res.data.total,
        },
      } as unknown as T;
    };

  return (
    <main className="page-shell" data-testid="admin-games-page">
      <PageHeader
        eyebrow="Manajemen"
        title="Toggle game"
        description="Aktifkan atau nonaktifkan game di katalog publik dan kelas. Game yang dinonaktifkan tersembunyi dari siswa sampai diaktifkan kembali."
      />
      <CatalogTogglePanel<GameRow>
        kind="game"
        fetchJson={wrappedFetch}
        columns={columns}
        filterOptions={visibilityFilters}
        searchPlaceholder="Cari judul game…"
      />
    </main>
  );
}
